import React from 'react';

import { Button, Icon, Intent, Tooltip } from '@blueprintjs/core';
import { ClientInactivityAlertNoticeResource } from '@data/api/clientInactivityAlert';
import { describeCondition } from '@pages/ClientInactivityAlertSettings/utils/clientInactivityAlertSettings';

export interface Props {
    notice: ClientInactivityAlertNoticeResource;
    // Offers the "(clear notice)" dismissal; omit it for a read-only warning
    onClear?: () => void;
    isClearing?: boolean;
    className?: string;
}

// A compact warning that sits beside a client name without changing the row or card layout:
// one inline icon at text size, and — where a dismissal is offered — one link-like text button.
// Nothing here reserves vertical space, so the avatar / company / plan / last-activity block it
// sits next to keeps the exact geometry it had.
const ClientInactivityAlertWarning: React.FC<Props> = ({ notice, onClear, isClearing = false, className }) => {
    const tooltipContent = (
        <div>
            <div>Inactive client</div>
            {notice.conditions.map((condition) => (
                <div key={condition.type}>{describeCondition(condition)}</div>
            ))}
        </div>
    );

    return (
        <span className={className}>
            <Tooltip content={tooltipContent}>
                <Icon
                    icon="warning-sign"
                    intent={Intent.WARNING}
                    size={14}
                    aria-label="Inactive client"
                    title="Inactive client"
                />
            </Tooltip>

            {onClear && (
                <Button
                    className="ml-small"
                    minimal
                    small
                    intent={Intent.PRIMARY}
                    // No padding or minimum size, so only the text itself is clickable and the
                    // icon beside it stays outside the hit area
                    style={{ padding: 0, minHeight: 0, minWidth: 0 }}
                    loading={isClearing}
                    disabled={isClearing}
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                        // The warning lives inside a link to the client, so the click must not
                        // navigate away from the list the operator is working through
                        e.preventDefault();
                        e.stopPropagation();
                        onClear();
                    }}
                    text="(clear notice)"
                />
            )}
        </span>
    );
};

export default ClientInactivityAlertWarning;
